import { reunionesOf, rotuloPeriodo, type EstadoReunion, type ReunionItem, type Snapshot } from '../lib/snapshot-types.js';
import { diaHora } from '../lib/fechas.js';
import type { CuboReuniones } from '../lib/hash-route.js';
import { hashDeRuta, navigate } from '../lib/hash-route.js';

interface Props {
  bucket: CuboReuniones;
  snapshot: Snapshot;
  locationId: string | null;
  zohoDataCenter: string | null;
}

export const ROTULO_ESTADO: Record<EstadoReunion, string> = {
  por_hacer: 'Por hacer',
  hecha: 'Hecha',
  no_show: 'No show',
  propuesta: 'Propuesta',
};

const TITULO_CUBO: Record<CuboReuniones, string> = {
  por_hacer: 'Reuniones por hacer',
  hecha: 'Reuniones hechas',
  no_show: 'No show',
  propuesta: 'Reuniones con propuesta',
  all: 'Reuniones',
};

/** Colores de los cuatro estados (los mismos en la tarjeta y en la lista). */
export const COLOR_ESTADO: Record<EstadoReunion, { fondo: string; texto: string; barra: string }> = {
  por_hacer: { fondo: 'var(--accent-soft)', texto: 'var(--accent-fg)', barra: '#2563eb' },
  hecha: { fondo: 'var(--ok-soft)', texto: 'var(--ok)', barra: '#16a34a' },
  no_show: { fondo: 'var(--danger-soft)', texto: 'var(--danger)', barra: '#dc2626' },
  propuesta: { fondo: 'rgba(109,40,217,.12)', texto: 'var(--info)', barra: '#6d28d9' },
};

export function ChipEstado({ estado }: { estado: EstadoReunion }) {
  const c = COLOR_ESTADO[estado];
  return <span style={{ background: c.fondo, color: c.texto, padding: '2px 7px', borderRadius: 4, fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>{ROTULO_ESTADO[estado]}</span>;
}

/** "lun 6 oct, 10:30", en la hora de Madrid (la del inquilino). */
/** «mar 29 sep, 11:00» en la zona del inquilino (el formato de toda la app, lib/fechas.ts). */
export function horaReunion(iso: string, zona = 'Europe/Madrid'): string {
  return diaHora(iso, zona);
}

/**
 * La lista de reuniones de la tarjeta: las MISMAS que ya trae el snapshot (no se
 * vuelve a pedir nada), agrupadas por estado. Al pulsar una de GHL se abre la
 * ficha del contacto; una de Zoho, su evento. Una grabada con Fluxo para Meet lleva
 * ademas «Ver transcripción», que la abre en la pagina de reuniones (#/reunion/<id>).
 */
export function MeetingsList({ bucket, snapshot, locationId, zohoDataCenter }: Props) {
  const r = reunionesOf(snapshot);
  const zona = snapshot.filtro?.zona ?? 'Europe/Madrid';
  const items = bucket === 'all' ? r.items : r.items.filter((i) => i.estado === bucket);
  // El orden en que lo dijo Joan: por hacer, hechas, no show y propuesta.
  const orden: EstadoReunion[] = ['por_hacer', 'hecha', 'no_show', 'propuesta'];
  const grupos = orden.map((e) => ({ estado: e, items: items.filter((i) => i.estado === e) })).filter((g) => g.items.length > 0);
  const periodo = snapshot.filtro ? rotuloPeriodo(snapshot.filtro.desde, snapshot.filtro.hasta) : '';
  const abrir = (it: ReunionItem): void => {
    if ((it.fuente === 'ghl' || it.fuente === 'meet') && locationId && it.contactId) {
      window.open(`https://app.gohighlevel.com/v2/location/${locationId}/contacts/detail/${it.contactId}`, '_blank', 'noopener');
    } else if (it.fuente === 'zoho') {
      window.open(`https://crm.zoho.${zohoDataCenter ?? 'eu'}/crm/EntityInfo.do?module=Events&id=${it.id.replace(/^z-/, '')}`, '_blank', 'noopener');
    }
  };
  return (
    <div className="overlay" onClick={() => navigate({ kind: 'snapshot' })}>
      <div className="overlay-card" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16, gap: 12 }}>
          <div>
            <div className="topbar-brand">{TITULO_CUBO[bucket]}</div>
            <h2 style={{ margin: '4px 0 0', fontSize: 18, fontWeight: 600 }}>{items.length} {items.length === 1 ? 'reunión' : 'reuniones'}</h2>
            {periodo && <div className="topbar-meta" style={{ marginTop: 4 }}>{periodo}</div>}
          </div>
          <button className="tab" onClick={() => navigate({ kind: 'snapshot' })}>✕ Cerrar</button>
        </div>
        {items.length === 0 && <div className="section-empty">Sin reuniones en este periodo.</div>}
        {grupos.map((g) => (
          <div key={g.estado} style={{ marginBottom: 14 }}>
            {bucket === 'all' && <div className="section-title" style={{ marginBottom: 6 }}>{ROTULO_ESTADO[g.estado]} · {g.items.length}</div>}
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {g.items.map((it) => (
                <li key={it.id} className="row-item" onClick={() => abrir(it)} style={{ cursor: it.contactId || it.fuente === 'zoho' ? 'pointer' : 'default' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="row-title" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.titulo}</span>
                      <ChipEstado estado={it.estado} />
                      {it.grabada && <span className="chip-grabada">Grabada</span>}
                      {it.grabacionId && (
                        <a
                          className="enlace-suave" href={hashDeRuta({ kind: 'reunion', id: it.grabacionId })}
                          onClick={(e) => e.stopPropagation()}
                        >Ver transcripción</a>
                      )}
                    </div>
                    <div className="row-sub">{it.persona}</div>
                    {it.resumen && <div className="reu-resumen">{it.resumen}</div>}
                  </div>
                  <div className="row-aside">{horaReunion(it.inicio, zona)}</div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
